import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import AdminLayout from '../layouts/AdminLayout';
import ZoomMeeting from '../components/zoom/ZoomMeeting';
import PageNotFound from '../pages/error/PageNotFound';
import { isFeedFeatureEnabled } from '../utils/featureFlags';

// pages/admin/dashboard, profile, settings, notifications, prompt assistant
import Dashboard from '../pages/admin/dashboard/Dashboard';
import Profile from '../pages/admin/profile/Profile';
import Settings from '../pages/admin/settings/Settings';
import Notifications from '../pages/admin/Notifications/Notifications';
import NotificationDetails from '../pages/admin/Notifications/NotificationDetails';
import PromptAssistant from '../pages/admin/PromptAssistant/PromptAssistant';
// pages/user/feed — shared feed pages, opened under /admin
import Feed from '../pages/user/feed/Feed';
import MyFeed from '../pages/user/feed/MyFeed';

// pages/admin/courses — catalogue, builder, content, analytics
import Courses from '../pages/admin/courses/Courses';
import AllCourses from '../pages/admin/courses/AllCourses';
import CreateCourse from '../pages/admin/courses/CreateCourse';
import CourseModule from '../pages/admin/courses/CourseModule';
import ModuleContent from '../pages/admin/courses/ModuleContent';
import CourseVideo from '../pages/admin/courses/CourseVideo';
import VideoPlayer from '../pages/admin/courses/CourseVideo/VideoPlayer';
import CourseDocument from '../pages/admin/courses/CourseDocument';
import CourseQuiz from '../pages/admin/courses/CourseQuiz';
import CourseSurvey from '../pages/admin/courses/CourseSurvey';
import CourseAnalytics from '../pages/admin/courses/CourseAnalytics';
import CourseAnalyticsAssessmentDetails from '../pages/admin/courses/CourseAnalyticsAssessmentDetails';
import CourseAnalyticsSurveyDetails from '../pages/admin/courses/CourseAnalyticsSurveyDetails';

// pages/admin/classroom — classrooms, assignments, assessments, live classes
import AllClassroom from '../pages/admin/classroom/AllClassroom';
import ClassroomDashboard from '../pages/admin/classroom/ClassroomDashboard';
import ClassroomTrainees from '../pages/admin/classroom/ClassroomTrainees';
import ClassroomAssignments from '../pages/admin/classroom/ClassroomAssignments';
import ClassroomAssignmentDetails from '../pages/admin/classroom/ClassroomAssignmentDetails';
import ClassroomAssessments from '../pages/admin/classroom/ClassroomAssessments';
import ClassroomAssessmentDetails from '../pages/admin/classroom/ClassroomAssessmentDetails';
import AssignmentQuiz from '../pages/admin/classroom/AssignmentQuiz';
import ClassroomAssignmentResult from '../pages/admin/classroom/ClassroomAssignmentResult';
import ClassroomLiveClasses from '../pages/admin/classroom/ClassroomLiveClasses';
// pages/user/classroom — community page shared with the learner classroom
import ClassroomCommunity from '../pages/user/classroom/Cummunity';

// pages/admin/trainees, pages/admin/certificates
import Trainees from '../pages/admin/trainees/trainees';
import TraineeAnalytics from '../pages/admin/trainees/TraineeAnalytics';
import AllCertificates from '../pages/admin/certificates/AllCertificates';

// pages/admin/roles_and_access, approvalRequest, questionBank
import RolesAndAccess from '../pages/admin/roles_and_access/RolesAndAccess';
import ApprovalRequest from '../pages/admin/approvalRequest/ApprovalRequest';
import QuestionBank from '../pages/admin/questionBank/QuestionBank';

function featurePage(enabled, page, fallbackPath) {
  return enabled ? page : <Navigate to={fallbackPath} replace />;
}

function AdminRoutes() {
  const feedEnabled = isFeedFeatureEnabled();

  return (
    <>
      <Routes>
        {/* Standalone page — no admin layout */}
        <Route path="/zoom-meeting" element={<ZoomMeeting />} />

        <Route path="/admin" element={<AdminLayout />}>
          {/* pages/admin/dashboard, pages/admin/profile */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />

          {/* pages/admin/courses */}
          <Route path="courses" element={<Courses />} />
          <Route path="AllCourses" element={<AllCourses />} />
          <Route path="courses/create" element={<CreateCourse />} />
          <Route path="courses/edit-create/:courseId" element={<CreateCourse />} />
          <Route path="courses/module/:courseId" element={<CourseModule />} />
          <Route path="courses/module/:courseId/content/:moduleId" element={<ModuleContent />} />
          <Route path="courses/module/:courseId/content/:moduleId/document/:contentId" element={<CourseDocument />} />
          <Route path="courses/module/:courseId/content/:moduleId/survey/:contentId" element={<CourseSurvey />} />
          <Route path="courses/module/:courseId/content/:moduleId/quiz/:contentId" element={<CourseQuiz />} />
          <Route path="courses/module/:courseId/content/:moduleId/video/:contentId" element={<VideoPlayer />} />

          {/* pages/admin/courses — analytics */}
          <Route path="courses/analytics/:courseId" element={<CourseAnalytics />} />
          <Route path="courses/analytics/:courseId/CourseAnalyticsAssessmentDetails/:assessmentId" element={<CourseAnalyticsAssessmentDetails />} />
          <Route path="courses/analytics/:courseId/CourseAnalyticsSurveyDetails/:surveyId" element={<CourseAnalyticsSurveyDetails />} />

          {/* pages/admin/courses — older paths kept so existing links still open */}
          <Route path="CourseModule" element={<CourseModule />} />
          <Route path="CourseModuleContent" element={<ModuleContent />} />
          <Route path="CourseVideo" element={<CourseVideo />} />
          <Route path="CourseDocument" element={<CourseDocument />} />
          <Route path="CourseQuiz" element={<CourseQuiz />} />
          <Route path="CourseSurvey" element={<CourseSurvey />} />

          {/* pages/admin/roles_and_access, pages/admin/approvalRequest */}
          <Route path="roles-and-access" element={<RolesAndAccess />} />
          <Route path="approvalRequest" element={<ApprovalRequest />} />

          {/* pages/admin/classroom */}
          <Route path="classrooms" element={<AllClassroom />} />
          <Route path="classrooms/classroom-dashboard/:classroomId" element={<ClassroomDashboard />} />
          <Route path="classrooms/classroom-dashboard/:classroomId/trainees/:classroomId" element={<ClassroomTrainees />} />
          <Route path="ClassroomAssignments" element={<ClassroomAssignments />} />
          <Route path="classrooms/classroom-dashboard/:classroomId/assingment/:assignmentId/ClassroomAssignmentDetails" element={<ClassroomAssignmentDetails />} />
          <Route path="classrooms/assignment-quiz/:classroomId/:assignmentId/:questionId" element={<AssignmentQuiz />} />
          <Route path="ClassroomAssessments" element={<ClassroomAssessments />} />
          <Route path="classrooms/classroom-dashboard/:classroomId/assessment/:assessmentId/ClassroomAssessmentDetails" element={<ClassroomAssessmentDetails />} />
          <Route path="classrooms/assignments/quiz-results" element={<ClassroomAssignmentResult />} />
          <Route path="ClassroomLiveClasses" element={<ClassroomLiveClasses />} />
          <Route path="ClassroomCommunity" element={<ClassroomCommunity />} />

          {/* pages/admin/questionBank */}
          <Route path="question-bank" element={<QuestionBank />} />

          {/* pages/admin/trainees */}
          <Route path="trainees" element={<Trainees />} />
          <Route path="traineesAnalytics/:traineeId" element={<TraineeAnalytics />} />

          {/* pages/user/feed */}
          <Route path="feed" element={featurePage(feedEnabled, <Feed basePath="/admin" />, '/admin/dashboard')} />
          <Route path="my-feed" element={featurePage(feedEnabled, <MyFeed />, '/admin/dashboard')} />

          {/* pages/admin/certificates */}
          <Route path="certificates" element={<AllCertificates />} />
          {/* pages/admin/settings — old announcement URL opens the Settings tab */}
          <Route path="announcement" element={<Navigate to="/admin/settings?activeTab=announcement" replace />} />

          {/* pages/admin/Notifications */}
          <Route path="notifications" element={<Notifications />} />
          <Route path="notifications/:id" element={<NotificationDetails />} />

          {/* pages/admin/settings, pages/admin/PromptAssistant */}
          <Route path="settings" element={<Settings />} />
          <Route path="prompt-assistant" element={<PromptAssistant />} />

          {/* pages/error */}
          <Route path="*" element={<PageNotFound />} />
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Routes>
      <ToastContainer limit={3} />
    </>
  );
}

export default AdminRoutes;
